import SimplePeer from 'simple-peer'
import { arrayInPlaceRemove } from '../utils'
import { ws_call_timeout_ms } from './Constants'
import { Logger, logger } from './Logger'
import { AudioTrackWithInfo } from '../UserContext'

interface MusePeerConstructor {
    sessionId: string
    userUrn: string
    username: string
    name: string | null
    connection: SimplePeer.Instance
    defaultStream: MediaStream
    callWasInitiatedByMe: boolean
    onExtraConnectionError: (code: string) => void
}

export type MuseTrackPresentationInfo = {
    appVersion: string
    displayId: string | null
}

type MuseTrackImmutableInfo = {
    sourceTrackId: string
    label: string
    isPresentation?: boolean
    presentationInfo?: MuseTrackPresentationInfo
}

export type MuseTrackMutableInfo = {
    isMutedByOwner?: boolean
    isRemoteDesktopControlEnabled?: boolean
    isRemoteDesktopControlAllowed?: boolean
    isVideoMirrored?: boolean
}

export type MuseTrackInfo = MuseTrackImmutableInfo & MuseTrackMutableInfo

export interface MuseRemoteAudioTrack {
    track: MediaStreamTrack
    stream: MediaStream
    info: MuseTrackInfo
    localVolumeCache: number
}

export interface MuseRemoteVideoTrack {
    track: MediaStreamTrack
    stream: MediaStream
    info: MuseTrackInfo
}

export type MuseOutgoingAudioTrack = {
    track: AudioTrackWithInfo
    filteredTrack: MediaStreamTrack
    gainNode: GainNode | null
}

export interface MuseMidiDeviceInfo {
    sourceId: string
    label: string
    isMuted: boolean
}

export class MusePeer {
    sessionId: string
    userUrn: string
    username: string
    name: string | null
    defaultStream: MediaStream

    // The connection instance.
    connection: SimplePeer.Instance

    // tracks coming from the peer.
    atracks: MuseRemoteAudioTrack[] = []
    vtracks: MuseRemoteVideoTrack[] = []
    midiDevices: MuseMidiDeviceInfo[] = []

    // tracks going out to the peer.
    out_atracks: MuseOutgoingAudioTrack[] = []

    sendqueue: { track: MediaStreamTrack; info: MuseTrackInfo }[] = []
    sending: { track: MediaStreamTrack; info: MuseTrackInfo; timeout: NodeJS.Timeout } | null = null

    reconnectInfo: {
        isReconnecting: boolean
        disconnectedTime: Date | null
        retryCt: number
    }

    callWasInitiatedByMe: boolean

    onExtraConnectionError: (code: string) => void

    private log: Logger

    constructor(c: MusePeerConstructor) {
        this.sessionId = c.sessionId
        this.userUrn = c.userUrn
        this.username = c.username
        this.name = c.name
        this.connection = c.connection
        this.defaultStream = c.defaultStream
        this.reconnectInfo = {
            isReconnecting: false,
            disconnectedTime: null,
            retryCt: 0,
        }
        this.callWasInitiatedByMe = c.callWasInitiatedByMe
        this.onExtraConnectionError = c.onExtraConnectionError

        this.log = logger.createChildLogger({
            name: 'MusePeer',
            sessionId: this.sessionId,
            userUrn: this.userUrn,
            username: this.username,
        })

        this.connection.on('data', (data_raw: string) => {
            let data
            try {
                data = JSON.parse(data_raw)
            } catch (e) {
                // Failed to parse.
                // console.error(`at MusePeer: Connection data channel: failed to parse data "${data_raw}" into JSON. %O`, e)
                return
            }

            if (data.type === 'ack-add-track-preamble') {
                this.log.info(
                    'track-negotiation',
                    `received "ack-add-track-preamble" for sending=` +
                        JSON.stringify(
                            this.sending == null
                                ? null
                                : {
                                      trackId: this.sending.track.id,
                                      trackLabel: this.sending.track.label,
                                      trackInfo: this.sending.info,
                                  }
                        )
                )
                this.log.info(
                    'track-negotiation',
                    `processing ack-add-track-preamble. sendqueue.length = ${this.sendqueue.length}.`
                )
                if (this.sending) {
                    this.log.info('track-negotiation', `this.sending exists; clearing its timeout.`)
                    clearTimeout(this.sending.timeout)
                }

                this.log.info('track-negotiation', `calling _addNextTrackFromSendqueue()`)
                this._addNextTrackFromSendqueue()
            }
        })
    }

    isConnectionReady(): boolean {
        if (this.connection) {
            return this.connection.connected
        } else {
            return false
        }
    }

    addTrack(track: MediaStreamTrack, info: MuseTrackInfo) {
        if (this.sending == null) {
            this.log.info(
                'addTrack',
                `this.sending is null; send track (id=${track.id} label="${info.label}") immediately.`
            )
            this._addTrack(track, info)
        } else {
            this.log.info(
                'addTrack',
                `this.sending is not null -- there is a track ` +
                    `being sent currently { id=${this.sending.track.id} label="${this.sending.info.label}" }. Adding track ` +
                    `(id=${track.id} label="${info.label}") to the sendqueue.`
            )
            this.log.info('addTrack', `sendqueue before: `, {
                sendqueue: this.sendqueue.map((x) => x.info),
            })

            if (this.sendqueue.filter((v) => v.info.label === info.label).length > 0) {
                arrayInPlaceRemove(this.sendqueue, (item) => {
                    this.log.info(
                        'addTrack',
                        `source label="${info.label}" already exists ` +
                            `in the sendqueue. Removing it from the sendqueue.`
                    )
                    return item.info.label === info.label
                })
            }
            this.sendqueue.push({ track, info })

            this.log.info('addTrack', `sendqueue after: `, {
                sendqueue: this.sendqueue.map((x) => x.info),
            })
        }
    }

    removeTrack(track: MediaStreamTrack, info: MuseTrackInfo) {
        try {
            this.connection.removeTrack(track, this.defaultStream)
        } catch (e) {
            this.log.error('removeTrack', `connection.removeTrack() threw an exception: `, { e })
        }

        // arrayInPlaceRemove(this.sendqueue, (item) => {
        //     this.log.info('removeTrack', `source label="${info.label}" exists ` +
        //         `in the sendqueue. Removing it from the sendqueue.`)
        //     return item.info.label === info.label
        // })
    }

    private _addTrack(track: MediaStreamTrack, info: MuseTrackInfo) {
        const TTL_ms = ws_call_timeout_ms
        const timeout = setTimeout(() => {
            this.log.error(
                'addTrack',
                `Track { id=${track.id} label=${info.label} } failed to send. ` +
                    `The peer timed out in returning a "ack-add-track-preamble" event. ` +
                    `Clearing all the queue-related states. Setting ERR_CONNECTION_FAILURE and ending connection.`
            )
            this.sendqueue = []
            this.sending = null

            this.onExtraConnectionError('ERR_CONNECTION_FAILURE')
            this.connection.end()
        }, TTL_ms)
        this.sending = { track, info, timeout }

        this.log.info(
            'addTrack',
            `Added track { id=${track.id} label=${info.label} }, sending event ` + `"add-track-preamble" to peer.`
        )
        try {
            this.connection.send(
                JSON.stringify({
                    type: 'add-track-preamble',
                    data: info,
                })
            )
        } catch (e) {
            this.log.error(
                'addTrack',
                `Attempted to send "add-track-preamble" message to peer but ` + `an exception was thrown:`,
                { e }
            )
            this.onExtraConnectionError('ERR_CONNECTION_FAILURE')
        }

        try {
            this.connection.addTrack(track, this.defaultStream)
        } catch (e) {
            this.log.error('addTrack', `connection.addTrack() threw an exception:`, { e })
            this.onExtraConnectionError('ERR_CONNECTION_FAILURE')
        }
    }

    private _addNextTrackFromSendqueue() {
        if (this.sendqueue.length > 0) {
            const { track, info } = this.sendqueue[0]
            this.sendqueue.shift()

            this.log.info('addNextTrackFromSendqueue', `queue is not empty. Next track ` + `in line: `, { info })

            // It looks like simple-peer doesn't like sending videos back-to-back. Detect that we're
            // doing this, then add a manual timeout.
            // Recall that `this.sending` is cleared yet, so it is a convenient way to know what
            // the track that we sent previously is.
            if (this.sending && this.sending.track.kind === 'video' && track.kind === 'video') {
                this.log.info(
                    'addNextTrackFromSendqueue',
                    `calling addTrack() with a 2-second delay because we're trying ` + `to add videos back-to-back.`
                )
                setTimeout(() => {
                    this.log.info('addNextTrackFromSendqueue', `calling addTrack().`)
                    this._addTrack(track, info)
                }, 2000)
            } else {
                this.log.info('addNextTrackFromSendqueue', `calling addTrack().`)
                this._addTrack(track, info)
            }
        } else {
            this.log.info('addNextTrackFromSendqueue', `queue is empty; we're done.`)
            this.sending = null
        }
    }

    setNegotiateAudioTracks(addedTracks: AudioTrackWithInfo[], removedTracks: AudioTrackWithInfo[]) {
        for (let atrack of removedTracks) {
            try {
                this.log.info(
                    'setNegotiateAudioTracks,removeTrack',
                    `peer=${this.username || this.userUrn} ` +
                        `detected removed audio track=${JSON.stringify({
                            deviceId: atrack.info.deviceId,
                            label: atrack.label,
                            trackId: atrack.id,
                        })}`
                )

                // Find the corresponding track in this.out_atracks by id.
                //const match = this.out_atracks.filter(a => a.track.info.deviceId === atrack.info.deviceId)[0]
                //arrayInPlaceRemove(this.out_atracks, a => a.track.info.deviceId === atrack.info.deviceId)
                const match = this.out_atracks.filter((a) => a.track.id === atrack.id)[0]
                arrayInPlaceRemove(this.out_atracks, (a) => a.track.id === atrack.id)
                if (match) {
                    this.log.info(
                        'setNegotiateAudioTracks,removeTrack',
                        `Found corresponding track in ` +
                            `this.out_atracks: (matched by the id=${atrack.id}): ` +
                            `{ filteredTrack.id=${match.filteredTrack.id}, label="${match.track.label}" }`
                    )
                    this.log.info(
                        'setNegotiateAudioTracks,removeTrack',
                        `Calling removeTrack on ` + `filteredTrack.id=${match.filteredTrack.id}`
                    )
                    this.log.info(
                        'setNegotiateAudioTracks,removeTrack',
                        `setNegotiateAudioTracks: this.sending=` +
                            JSON.stringify(
                                this.sending == null
                                    ? null
                                    : {
                                          trackId: this.sending.track.id,
                                          trackLabel: this.sending.track.label,
                                          trackInfo: this.sending.info,
                                      }
                            )
                    )
                    // Check: is this track currently sending? If so, delay removeTrack until sending has completed.
                    if (this.sending?.info.sourceTrackId === atrack.id) {
                        this.log.info(
                            'setNegotiateAudioTracks,removeTrack',
                            `setNegotiateAudioTracks: ` +
                                `Trying to remove this track ` +
                                `when negotiation hasn't completed! Delaying the removal of this track.`
                        )
                        setTimeout(() => {
                            this.removeTrack(match.filteredTrack, {
                                sourceTrackId: match.track.id,
                                label: atrack.label,
                                isPresentation: false,
                            })
                        }, 5000) // Not very sophisticated but gets the job done.
                    } else {
                        this.removeTrack(match.filteredTrack, {
                            sourceTrackId: match.track.id,
                            label: atrack.label,
                            isPresentation: false,
                        })
                    }
                }
            } catch (e) {
                this.log.error(
                    'setNegotiateAudioTracks,removeTrack',
                    `An exception occurred while removing track ` + `{ id=${atrack.id} label="${atrack.label}"}`,
                    { e }
                )
            }
        }

        for (let atrack of addedTracks) {
            // attach gainNode
            const isMono = atrack.info.channelCount === 1
            const { filteredTrack, gainNode } = this.attachGainNode(atrack, { isMono })

            this.log.info(
                'setNegotiateAudioTracks',
                `peer="${this.username || this.userUrn}" ` +
                    `detected new audio track=${JSON.stringify({
                        deviceId: atrack.info.deviceId,
                        label: atrack.label,
                        trackId: atrack.id,
                        filteredTrack: filteredTrack.id,
                    })}`
            )

            this.out_atracks.push({
                track: atrack,
                filteredTrack,
                gainNode,
            })

            this.log.info(
                'setNegotiateAudioTracks',
                `peer=${this.username || this.userUrn} ` + `adding track with id=${atrack.id} to peer connection.`
            )
            this.addTrack(filteredTrack, {
                sourceTrackId: atrack.id,
                label: atrack.info.displayLabel,
            })

            // let matchingRemovedTracks = removedTracks.filter(t => t.info.deviceId === atrack.info.deviceId)
            // matchingRemovedTracks.forEach(removedTrack => {
            //     this.log.info('setNegotiateAudioTracks', `For addedTrack { id=${atrack.id} label="${atrack.label}" deviceId=${atrack.info.deviceId} }, ` +
            //         `found matching removedTrack { id=${removedTrack.id} label="${removedTrack.label}" deviceId=${removedTrack.info.deviceId} }.`)
            //     arrayInPlaceRemove(removedTracks, (x) => x.id === removedTrack.id)
            //     setTimeout(() => {
            //     }, 3000)
            // })
        }
    }

    addMidiDevice(info: MuseMidiDeviceInfo) {
        this.connection.send(
            JSON.stringify({
                type: 'add-midi-advertise',
                data: info,
            })
        )
    }

    clearMidiDevices() {
        this.connection.send(
            JSON.stringify({
                type: 'clear-midi-advertise',
            })
        )
    }

    disconnect() {
        try {
            this.connection.end()

            this.atracks.forEach((atrack) => atrack.track.stop())
            this.vtracks.forEach((vtrack) => vtrack.track.stop())
            this.midiDevices = []

            this.out_atracks.forEach((atrack) => {
                atrack.filteredTrack.stop()
                atrack.gainNode?.disconnect()
            })
        } catch (e) {
            console.error(`An exception was thrown while cleaning up a disconnected peer=${this.userUrn}.`)
            console.error(e)
        }
    }

    toPrintableObject() {
        return {
            userUrn: this.userUrn,
            isConnectionReady: this.isConnectionReady(),
            connection: this.connection == null ? '<null>' : '<exists>',
            atracks: this.atracks.map((t) => {
                return {
                    track: trackToPrintableObject(t.track),
                    //originalTrack: t.originalTrack ? trackToPrintableObject(t.originalTrack) : null,
                    stream: `<id=${t.stream.id}>`,
                }
            }),
            vtracks: this.vtracks.map((t) => {
                return {
                    track: trackToPrintableObject(t.track),
                    stream: `<id=${t.stream.id}>`,
                }
            }),
        }
    }

    private attachGainNode(track: MediaStreamTrack, options: { isMono: boolean }) {
        const endStream = new MediaStream([track])
        const context = new window.AudioContext()
        const microphone = context.createMediaStreamSource(endStream)

        const gainNode = context.createGain()
        gainNode.gain.value = 1

        const destination = context.createMediaStreamDestination()
        const outputStream = destination.stream

        microphone.connect(gainNode)
        gainNode.connect(destination)

        const filteredTrack = outputStream.getAudioTracks()[0]

        return { filteredTrack, gainNode }
    }
}

export function trackToPrintableObject(track: MediaStreamTrack | null) {
    if (track == null) {
        return null
    } else {
        return {
            id: track.id,
            kind: track.kind,
            muted: track.muted,
            enabled: track.enabled,
        }
    }
}
